/**
 * The eight synthesized voices.
 *
 * Two rules every voice follows, both learned the hard way:
 *
 *  - Envelopes start at 0.0001 and use exponential ramps. A bare `gain.value = x`
 *    is a step change and clicks audibly on every phone.
 *  - Every node is disconnected on `ended`. A 30-round game creates thousands of
 *    nodes and Safari does not reclaim connected ones eagerly — that becomes
 *    crackle and climbing memory.
 *
 * An AudioBufferSourceNode is single-use, so each play builds a fresh one; only
 * the noise *buffer* is shared.
 */

import { withAudio } from './context';

const NEAR_ZERO = 0.0001;

interface Ctx {
  ctx: AudioContext;
  master: GainNode;
  noise: AudioBuffer;
}

/** Wire a chain of nodes and disconnect them all once the source finishes. */
function playThrough(
  source: AudioScheduledSourceNode,
  nodes: AudioNode[],
  master: GainNode,
  start: number,
  stop: number
): void {
  let previous: AudioNode = source;
  for (const node of nodes) {
    previous.connect(node);
    previous = node;
  }
  previous.connect(master);

  source.onended = () => {
    source.disconnect();
    for (const node of nodes) node.disconnect();
  };

  source.start(start);
  source.stop(stop);
}

function noiseSource({ ctx, noise }: Ctx): AudioBufferSourceNode {
  const source = ctx.createBufferSource();
  source.buffer = noise;
  // Start somewhere random in the buffer so repeated bursts are not identical.
  return source;
}

function envelope(ctx: AudioContext, peak: number, start: number, attack: number, decay: number) {
  const gain = ctx.createGain();
  gain.gain.setValueAtTime(NEAR_ZERO, start);
  gain.gain.exponentialRampToValueAtTime(peak, start + attack);
  gain.gain.exponentialRampToValueAtTime(NEAR_ZERO, start + attack + decay);
  return gain;
}

/** A short tone. Used by every pitched cue. */
function tone(
  c: Ctx,
  start: number,
  frequency: number,
  {
    type = 'sine',
    peak = 0.18,
    attack = 0.008,
    decay = 0.09,
  }: { type?: OscillatorType; peak?: number; attack?: number; decay?: number } = {}
): void {
  const osc = c.ctx.createOscillator();
  osc.type = type;
  osc.frequency.setValueAtTime(frequency, start);
  const gain = envelope(c.ctx, peak, start, attack, decay);
  playThrough(osc, [gain], c.master, start, start + attack + decay + 0.02);
}

/**
 * A card landing on the felt: a filtered noise burst.
 *
 * The ±15% frequency jitter is not cosmetic. Without it, thirteen taps in a row
 * sound like a drum machine instead of like cards.
 */
export function voiceCardPlace(start: number): void {
  withAudio((c) => {
    const source = noiseSource(c);
    const jitter = 0.85 + Math.random() * 0.3;

    const high = c.ctx.createBiquadFilter();
    high.type = 'highpass';
    high.frequency.value = 400 * jitter;

    const band = c.ctx.createBiquadFilter();
    band.type = 'bandpass';
    band.frequency.value = 1800 * jitter;
    band.Q.value = 0.7;

    const gain = envelope(c.ctx, 0.5, start, 0.003, 0.07);
    playThrough(source, [high, band, gain], c.master, start, start + 0.12);
  });
}

/** The finished trick being gathered up: noise swept downward. */
export function voiceTrickSweep(start: number, mine: boolean): void {
  withAudio((c) => {
    const source = noiseSource(c);

    const band = c.ctx.createBiquadFilter();
    band.type = 'bandpass';
    band.Q.value = 1.2;
    band.frequency.setValueAtTime(3000, start);
    band.frequency.exponentialRampToValueAtTime(700, start + 0.26);

    const gain = envelope(c.ctx, 0.35, start, 0.02, 0.3);
    playThrough(source, [band, gain], c.master, start, start + 0.4);

    // Your own trick gets a small reward blip on top.
    if (mine) tone(c, start + 0.18, 880, { peak: 0.16, decay: 0.12 });
  });
}

/** Two soft blips: you are on the clock. */
export function voiceYourTurn(start: number): void {
  withAudio((c) => {
    tone(c, start, 660, { peak: 0.18, decay: 0.07 });
    tone(c, start + 0.11, 880, { peak: 0.18, decay: 0.08 });
  });
}

/** Bid made exactly: a rising interval. */
export function voiceBidExact(start: number): void {
  withAudio((c) => {
    tone(c, start, 587, { type: 'triangle', peak: 0.2, decay: 0.12 });
    tone(c, start + 0.09, 880, { type: 'triangle', peak: 0.2, decay: 0.16 });
  });
}

/**
 * Bid missed: a falling interval, and deliberately quieter than the reward.
 * A punishment sound that is louder than the praise makes an app feel mean.
 */
export function voiceBidMissed(start: number): void {
  withAudio((c) => {
    tone(c, start, 587, { type: 'triangle', peak: 0.14, decay: 0.14 });
    tone(c, start + 0.1, 392, { type: 'triangle', peak: 0.14, decay: 0.2 });
  });
}

/**
 * "You cannot play that": a low thud with a noise tick.
 *
 * 190 Hz, not 110 — a phone speaker rolls off hard below about 500 Hz, and a
 * 110 Hz thud is simply inaudible on a handset lying on a table. The noise
 * transient is what actually carries through a one-centimetre driver.
 */
export function voiceRefused(start: number): void {
  withAudio((c) => {
    tone(c, start, 190, { peak: 0.3, attack: 0.004, decay: 0.16 });

    const source = noiseSource(c);
    const low = c.ctx.createBiquadFilter();
    low.type = 'lowpass';
    low.frequency.value = 2200;
    const gain = envelope(c.ctx, 0.22, start, 0.003, 0.03);
    playThrough(source, [low, gain], c.master, start, start + 0.06);
  });
}

/** Cards being dealt: a handful of quick high ticks. */
export function voiceDeal(start: number, ticks: number): void {
  withAudio((c) => {
    for (let i = 0; i < ticks; i++) {
      const at = start + i * 0.055;
      const source = noiseSource(c);
      const high = c.ctx.createBiquadFilter();
      high.type = 'highpass';
      high.frequency.value = 2500;
      const gain = envelope(c.ctx, 0.12, at, 0.002, 0.018);
      playThrough(source, [high, gain], c.master, at, at + 0.04);
    }
  });
}

/** Game over: a short arpeggio, under 600 ms so it does not outstay its welcome. */
export function voiceGameOver(start: number): void {
  withAudio((c) => {
    [523, 659, 784, 1046].forEach((frequency, index) => {
      tone(c, start + index * 0.09, frequency, {
        type: 'triangle',
        peak: 0.2,
        decay: 0.22,
      });
    });
  });
}

/** Someone joined the lobby: one soft blip. */
export function voicePlayerJoined(start: number): void {
  withAudio((c) => {
    tone(c, start, 784, { peak: 0.14, decay: 0.1 });
  });
}
